import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "../App.css";
import { supabase } from "../supabaseClient";
import DigitalCard from "../components/DigitalCard";
import { DEFAULT_CARD_COLOR } from "../constants/cardDefaults";
import { trackGoal } from "../utils/datafast";
import { sortLicensesByState } from "../utils/licenses";

const API_BASE = process.env.REACT_APP_API_URL || "";

export default function PublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [profileId, setProfileId] = useState("");
  const [isVanity, setIsVanity] = useState(false);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cardColor, setCardColor] = useState(DEFAULT_CARD_COLOR);
  const viewTrackedRef = useRef(null);

  const initials = useMemo(() => {
    if (!profile) return "U";
    const first = profile.firstName?.[0] || "";
    const last = profile.lastName?.[0] || "";
    return (first + last).trim() || "U";
  }, [profile]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      let profileData = null;
      let profErr = null;
      const profileColumnSets = [
        "id,first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,public_link,show_email,show_phone,show_company,card_color,subscription_status,subscription_tier,title,links,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,public_link,show_email,show_phone,show_company,card_color,subscription_status,subscription_tier,title,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,public_link,show_email,show_phone,show_company,card_color,subscription_status,subscription_tier,title,links,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,card_background_photo,public_link,show_email,show_phone,show_company,card_color,subscription_status,subscription_tier,title,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,card_color,title,links,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,card_color,title,business_street,business_city,business_state,business_zip,business_county,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,card_color,title,links,twitter,facebook,instagram,linkedin",
        "id,first_name,last_name,email,phone,company_agency,profile_photo,public_link,show_email,show_phone,show_company,card_color,title,twitter,facebook,instagram,linkedin",
      ];
      try {
        for (const cols of profileColumnSets) {
          const res = await supabase.from("profiles").select(cols).eq("public_slug", slug).maybeSingle();
          if (!res.error && res.data) {
            profileData = res.data;
            profErr = null;
            break;
          }
          profErr = res.error;
        }
      } catch (err) {
        profErr = err;
      }
      if (profErr) {
        setError(profErr.message);
        setLoading(false);
        return;
      }
      if (!profileData) {
        setError("Profile not found.");
        setLoading(false);
        return;
      }

      // Resolve profile photo (signed if stored in bucket)
      let photoUrl = null;
      let cardBackgroundUrl = null;
      if (profileData.profile_photo) {
        if (profileData.profile_photo.startsWith("http")) {
          photoUrl = profileData.profile_photo;
        } else {
          const { data: signedPhoto } = await supabase.storage
            .from("profile_photos")
            .createSignedUrl(profileData.profile_photo, 3600);
          photoUrl = signedPhoto?.signedUrl || null;
        }
      }
      if (profileData.card_background_photo) {
        if (profileData.card_background_photo.startsWith("http")) {
          cardBackgroundUrl = profileData.card_background_photo;
        } else {
          const { data: signedBg } = await supabase.storage
            .from("profile_photos")
            .createSignedUrl(profileData.card_background_photo, 3600);
          cardBackgroundUrl = signedBg?.signedUrl || null;
        }
      }

      const tier = String(profileData.subscription_tier || "").toLowerCase();
      const status = String(profileData.subscription_status || "").toLowerCase();
      const isPaid =
        tier === "pro" ||
        tier === "elite" ||
        status === "pro" ||
        status === "elite" ||
        status === "active" ||
        status === "trialing";

      // Build profile object
      const fallbackLink = `${window.location.origin}/p/${slug}`;
      const publicLink =
        profileData.public_link && profileData.public_link !== "-" ? profileData.public_link : fallbackLink;
      setProfileId(profileData.id || "");
      setIsVanity(Boolean(profileData.public_link && profileData.public_link !== "-" && profileData.public_link !== fallbackLink));

      const resolvedCardColor = isPaid ? profileData.card_color || DEFAULT_CARD_COLOR : DEFAULT_CARD_COLOR;
      const resolvedBackground = isPaid ? cardBackgroundUrl : null;

      const builtProfile = {
        firstName: profileData.first_name || "",
        lastName: profileData.last_name || "",
        email: profileData.email || "",
        phone: profileData.phone || "",
        company: profileData.company_agency || "",
        profilePhoto: photoUrl,
        publicLink,
        cardColor: resolvedCardColor,
        cardBackgroundUrl: resolvedBackground,
        title: profileData.title || "",
        links: Array.isArray(profileData.links) ? profileData.links : [],
        businessStreet: profileData.business_street || "",
        businessCity: profileData.business_city || "",
        businessState: profileData.business_state || "",
        businessZip: profileData.business_zip || "",
        businessCounty: profileData.business_county || "",
        twitter: profileData.twitter || "",
        facebook: profileData.facebook || "",
        instagram: profileData.instagram || "",
        linkedin: profileData.linkedin || "",
        visibility: {
          email: profileData.show_email ?? true,
          phone: profileData.show_phone ?? true,
          company: profileData.show_company ?? true,
          title: true,
          businessAddress: true,
          twitter: true,
          facebook: true,
          instagram: true,
          linkedin: true,
        },
      };
      setProfile(builtProfile);
      setCardColor(builtProfile.cardColor);

      // Get verified licenses
      const { data: licenseData, error: licErr } = await supabase
        .from("licenses")
        .select("id,industry,state,category,status,supporting_document,license_number,verified")
        .eq("user_id", profileData.id)
        .eq("removed", false)
        .or("verified.eq.true,status.ilike.active")
        .order("state", { ascending: true })
        .order("industry", { ascending: true })
        .order("category", { ascending: true });
      if (!licErr && licenseData) {
        const licensesWithDocs = await Promise.all(
          licenseData.map(async (l) => {
            let docUrl = null;
            if (l.supporting_document) {
              if (l.supporting_document.startsWith("http")) {
                docUrl = l.supporting_document;
              } else {
                const { data: signedDoc } = await supabase.storage
                  .from("license_docs")
                  .createSignedUrl(l.supporting_document, 3600);
                docUrl = signedDoc?.signedUrl || null;
              }
            }
            return {
              id: l.id,
              industry: l.industry,
              state: l.state,
              category: l.category,
              status: l.status,
              docUrl,
              supportingDocument: l.supporting_document,
              licenseNumber: l.license_number,
            };
          })
        );
        setLicenses(sortLicensesByState(licensesWithDocs));
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  const stats = useMemo(() => {
    const active = licenses.filter((l) => (l.status || "").toLowerCase() === "active").length;
    const pending = licenses.filter((l) => (l.status || "").toLowerCase() === "pending").length;
    const issues = licenses.filter((l) => (l.status || "").toLowerCase() === "issues").length;
    const expiring = 0;
    return { active, pending, issues, expiring };
  }, [licenses]);

  useEffect(() => {
    if (!slug || !profile) return;
    const logOpen = async () => {
      try {
        await fetch(`${API_BASE}/api/lead-event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            publicSlug: slug,
            type: "card_opened",
            deviceType: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            )
              ? "mobile"
              : "desktop",
          }),
        });
      } catch (e) {
        // ignore
      }
    };
    const logProfileView = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token || "";
        await fetch(`${API_BASE}/api/leads`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({
            publicSlug: slug,
            leadType: "profile_view",
            viewTargetType: "profile",
            viewTargetLabel: "Profile",
            deviceType: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent || ""
            )
              ? "mobile"
              : "desktop",
          }),
        });
      } catch (e) {
        // ignore
      }
    };
    logOpen();
    logProfileView();
  }, [slug, profile]);

  useEffect(() => {
    if (!slug || !profileId) return;
    if (viewTrackedRef.current === slug) return;
    const trackProfileView = async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const viewerId = sessionData?.session?.user?.id || "";
      if (viewerId && viewerId === profileId) {
        viewTrackedRef.current = slug;
        return;
      }
      const viewerType = viewerId ? "authenticated" : "anonymous";
      const storageKey = `datafast_profile_visit_${profileId}`;
      let seen = false;
      try {
        seen = !!sessionStorage.getItem(storageKey);
      } catch {
        // ignore
      }
      if (seen) {
        trackGoal("profile_return_visit", { profile_id: profileId });
      } else {
        trackGoal("profile_viewed", { profile_id: profileId, viewer_type: viewerType });
        try {
          sessionStorage.setItem(storageKey, "1");
        } catch {
          // ignore
        }
      }
      if (isVanity) {
        trackGoal("vanity_url_viewed", { profile_id: profileId });
      }
      viewTrackedRef.current = slug;
    };
    trackProfileView();
  }, [slug, profileId, isVanity]);

  return (
    <div className="public-shell embedded" style={{ minHeight: "100vh", paddingTop: 24 }}>
      <main className="hero embedded-hero" id="main-content">
        <div className="hero-inner" style={{ maxWidth: 520 }}>
          <h1 className="sr-only" data-page-heading="true">Public profile</h1>
          {loading && <p className="muted">Loading...</p>}
          {error && <p className="error-message">{error}</p>}
          {!loading && !error && profile && (
              <DigitalCard
                profile={profile}
                initials={initials}
                licenses={licenses}
                stats={stats}
                cardColor={cardColor}
                cardBackgroundUrl={profile.cardBackgroundUrl}
                cardColorDark="#c0d4ff"
                publicSlug={slug}
                profileId={profileId}
              />
          )}
        </div>
      </main>
    </div>
  );
}
